import React, {useState, useContext, useEffect} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import "./AppDomainCreation.css";
import {
  AuxiliaryToolTip,
  Button,
  DropDown,
  DropDownOption,
  Icon,
  IconButton,
  ModalContext,
  TextArea,
  TextInput,
} from "@spg/component_library";
import {SnackbarContext} from "../../reusables/SnackbarProvider";
import API, {Resolution} from "../../logic/API";
import {Application, ApplicationDomain, DataStream} from "../../logic/APITypes";
import chevronIcon from "../../images/chevron.svg";
import deleteIcon from "../../images/trash.svg";
import editIcon from "../../images/edit.svg";
import plusIcon from "../../images/plus.svg";
import {useNavigate} from "react-router-dom";
import {AppDomainDataStreamCreation} from "./AppDomainDataStreamCreation";
import {KeycloakContext} from "../utility/KeycloakProvider";
import {AppSettingsContext} from "../utility/AppSettingsProvider";

type DataStreamWithWaiting = DataStream & {waitingForDeletion: boolean};

/**
 * A page with a form allowing for the creation of app domains.
 */
function AppDomainCreation(props: {
  alternative: "create" | "view" | "edit";
  viewEditInfo?: {
    onSuccessfulEdit: (
      id: string,
      newApp: Omit<ApplicationDomain, "id">,
    ) => void;
    onBack: () => void;
    initialAppDomain: Resolution<ApplicationDomain>;
  };
  onSuccessfulCreation?: (domain: ApplicationDomain) => void;
  onCancel?: () => void;
}) {
  const addSnackbar = useContext(SnackbarContext).addSnackbar;
  const modalContext = useContext(ModalContext);
  const {keycloak, initialized} = useContext(KeycloakContext);
  const {wikiUrl} = useContext(AppSettingsContext);
  const intl = useIntl();
  const navigate = useNavigate();

  const initialAppDomain = props.viewEditInfo?.initialAppDomain.payload;

  const [alternative, setAlternative] = useState(props.alternative);
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string>();
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState(false);
  const [appOptionsError, setAppOptionsError] = useState<string>();
  const [appOptions, setAppOptions] = useState<DropDownOption<string>[]>();
  const [app, setApp] = useState<null | DropDownOption<string>>(null);
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [appDomainStreams, setAppDomainStreams] = useState<
    DataStreamWithWaiting[]
  >([]);
  const [isAdmin, setIsAdmin] = useState(false);

  const unmodifiedInputs =
    name === initialAppDomain?.name &&
    description === initialAppDomain?.description;

  const emptyInputs = name === "" && description === "" && app == null;

  const validInputs = [name, description].every((input) => input.length > 0);

  const applicationsPageIsOrigin =
    localStorage.getItem("appDomainCreationOriginPage") ===
    "other/applications";

  const getAppOptions = async () => {
    API.getAssignedApps(false).then((response) => {
      if (response.payload) {
        const appOptionsNew: {
          value: string;
          label: string;
        }[] = response.payload.map((app: Application) => ({
          value: app.id.toString(),
          label: app.name,
        }));
        if (appOptionsNew.length === 0) {
          setAppOptionsError("appDomainCreation.apps.empty.text");
        }
        if (app && !appOptionsNew.some((option) => option.value == app.value)) {
          setApp(null);
        } else {
          setAppOptionsError(undefined);
        }
        if (appOptionsNew !== appOptions) setAppOptions(appOptionsNew);
      } else {
        setAppOptionsError("appDomainCreation.apps.error.text");
      }
    });
  };

  const setInitialStates = () => {
    setName(initialAppDomain?.name || "");
    setDescription(initialAppDomain?.description || "");
    setApp(
      initialAppDomain ? {value: "", label: initialAppDomain?.app_name} : null,
    );
    setAppDomainStreams(
      initialAppDomain?.data_streams.map((stream) => ({
        ...stream,
        waitingForDeletion: false,
      })) || [],
    );
  };

  const onCreate = () => {
    if (!app) return false;
    const appDomain = {
      name: name,
      description: description,
      app_id: app.value,
    };

    setWaitingForResponse(true);

    API.createAppDomain(appDomain).then((response) => {
      setWaitingForResponse(false);
      if (response.success && response.payload) {
        if (props.onSuccessfulCreation)
          props.onSuccessfulCreation({
            ...appDomain,
            id: response.payload.id,
            app_name: app.label,
            data_streams: [],
          });
        addSnackbar({
          variant: "timeout",
          text: intl.formatMessage({
            id: "appDomainCreation.snackbar.create.success.text",
          }),
          timeout: 7000,
        });
      } else {
        if (
          response.errorReason &&
          !(typeof response.errorReason === "string")
        ) {
          const errorLocations = response.errorReason
            .filter((d) => d.loc && d.loc.length > 0)
            .map((d) => (d as {loc: string[]}).loc[1]);
          if (errorLocations.includes("name"))
            setNameError("appDomainCreation.name.error.used");
          if (errorLocations.includes("app_id") && response.httpCode === 404)
            setAppOptionsError("appDomainCreation.apps.error.unknown");
          else if (errorLocations.includes("app_id"))
            setAppOptionsError("appDomainCreation.apps.error.used");
        }
        addSnackbar({
          variant: "close",
          text: intl.formatMessage({
            id: "appDomainCreation.snackbar.create.error.text",
          }),
          hasCloseButton: true,
        });
      }
    });
  };

  /** Handles editing of the AppDomain. */
  const onUpdate = () => {
    modalContext.showModal({
      idBase: "appDomainCreationSave",
      headline: intl.formatMessage({
        id: "appDomainCreation.modal.save.headline",
      }),
      body: intl.formatMessage({id: "appDomainCreation.modal.save.text"}),
      primaryButton: {
        name: intl.formatMessage({id: "appDomainCreation.modal.save.save"}),
        onClick: () => {
          if (!initialAppDomain || !props.viewEditInfo) return;
          const appDomain = {
            name: name,
            description: description,
            app_name: initialAppDomain.app_name,
            data_streams: appDomainStreams,
          };
          setWaitingForResponse(true);
          API.updateAppDomain(initialAppDomain.id, appDomain).then(
            (response) => {
              if (response.success) {
                if (props.viewEditInfo && initialAppDomain)
                  props.viewEditInfo.onSuccessfulEdit(
                    initialAppDomain.id,
                    appDomain,
                  );
                setAlternative("view");
              } else {
                if (
                  response.errorReason &&
                  !(typeof response.errorReason === "string")
                ) {
                  const errorLocations = response.errorReason
                    .filter((d) => d.loc && d.loc.length > 0)
                    .map((d) => (d as {loc: string[]}).loc[1]);
                  if (errorLocations.includes("name"))
                    setNameError("appDomainCreation.name.error.used");
                }
                addSnackbar({
                  variant: "close",
                  text: intl.formatMessage({
                    id: "appDomainCreation.snackbar.edit.error.text",
                  }),
                  hasCloseButton: true,
                });
              }
              setWaitingForResponse(false);
            },
          );
        },
      },
      secondaryButton: {
        name: intl.formatMessage({id: "appDomainCreation.modal.save.cancel"}),
        onClick: () => undefined,
      },
    });
  };

  const openStreamCreation = (stream?: DataStream) => {
    modalContext.showModal({
      idBase: "appDomainCreationStreamsCreate",
      headline: intl.formatMessage({
        id: `appDomainCreation.modal.streams.${
          stream ? "edit" : "create"
        }.headline`,
      }),
      body: (
        <div className="modalText">
          <FormattedMessage
            id={`appDomainCreation.modal.streams.${
              stream ? "edit" : "create"
            }.text`}
          />
        </div>
      ),
      // Buttons aren't displayed, instead they are managed as part of the additionalContent
      primaryButton: {
        name: "",
        onClick: () => undefined,
      },
      secondaryButton: {
        name: "",
        onClick: () => undefined,
      },
      additionalContent: (
        <AppDomainDataStreamCreation
          domainId={props.viewEditInfo?.initialAppDomain.payload?.id || ""}
          stream={stream}
          onConfirm={(newStream) => {
            modalContext.hideModal();
            if (!initialAppDomain || !newStream) return;
            const appDomain = {
              name: name,
              description: description,
              app_name: initialAppDomain.app_name,
              data_streams: [...appDomainStreams, newStream],
            };
            if (props.viewEditInfo && initialAppDomain)
              props.viewEditInfo.onSuccessfulEdit(
                initialAppDomain.id,
                appDomain,
              );
          }}
          onCancel={() => {
            modalContext.hideModal();
          }}
        />
      ),
    });
  };

  const deleteStream = (stream: DataStream, index: number) => {
    modalContext.showModal({
      idBase: "appDomainCreationStreamsDelete",
      headline: intl.formatMessage({
        id: "appDomainCreation.modal.streams.delete.headline",
      }),
      body: intl.formatMessage(
        {
          id: "appDomainCreation.modal.streams.delete.body",
        },
        {
          streamName: stream.name,
        },
      ),
      primaryButton: {
        name: intl.formatMessage({
          id: "appDomainCreation.modal.streams.delete.confirm",
        }),
        onClick: () => {
          if (!initialAppDomain) return;
          setAppDomainStreams((oldStreams) => {
            const newStreams = [...oldStreams];
            newStreams[index] = {
              ...oldStreams[index],
              waitingForDeletion: true,
            };
            return newStreams;
          });
          API.deleteDataStream(initialAppDomain.id, stream.id).then(
            (response) => {
              setAppDomainStreams((oldStreams) => {
                const newStreams = [...oldStreams];
                if (response.success) newStreams.splice(index, 1);
                else
                  newStreams[index] = {
                    ...oldStreams[index],
                    waitingForDeletion: false,
                  };
                return newStreams;
              });
              if (response.success)
                addSnackbar({
                  text: intl.formatMessage({
                    id: "appDomainCreation.modal.streams.delete.success",
                  }),
                  variant: "timeout",
                  timeout: 7000,
                });
              else
                addSnackbar({
                  text: intl.formatMessage({
                    id: "appDomainCreation.modal.streams.delete.error",
                  }),
                  variant: "close",
                  hasCloseButton: true,
                });
            },
          );
        },
      },
      secondaryButton: {
        name: intl.formatMessage({
          id: "appDomainCreation.modal.streams.delete.cancel",
        }),
        onClick: () => null,
      },
    });
  };

  useEffect(() => {
    if (props.alternative === "create") {
      getAppOptions();
    }
  }, []);

  useEffect(setInitialStates, [
    props.viewEditInfo?.initialAppDomain,
    appOptions,
  ]);

  useEffect(() => {
    setIsAdmin(keycloak.hasResourceRole("DM Administration"));
  }, [initialized]);

  return (
    <div className={"appDomainContainer " + alternative}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <h1 id="appDomainCreationTitle">
          {alternative === "create" ? (
            <span>
              <FormattedMessage
                id="appDomainCreation.formTitle"
                values={{b: (chunks) => <b>{chunks}</b>}}
              />
            </span>
          ) : (
            <>
              {alternative !== "edit" && (
                <span>
                  <IconButton
                    idBase={"appDomainCreationBack"}
                    onClick={
                      applicationsPageIsOrigin
                        ? () => navigate("/other/applications")
                        : () => props.viewEditInfo?.onBack()
                    }
                    iconProps={{
                      href: chevronIcon + "#chevronLeft",
                      title: intl.formatMessage({
                        id: "appDomainCreation.formTitle.back.button",
                      }),
                      strokeColor: "black",
                    }}
                    hoverColor="#ff3d00"
                    buttonSize={{height: 36, width: 36}}
                  />
                </span>
              )}
              <span className="appDomainCreationTitle">
                <FormattedMessage
                  id="appDomainCreation.formTitle.edit"
                  values={{
                    b: (chunks) => <b>{chunks}</b>,
                    appDomainName: initialAppDomain?.name || "",
                  }}
                />
                {alternative === "view" && isAdmin && (
                  <IconButton
                    idBase={"appDomainCreationEdit"}
                    onClick={() => setAlternative("edit")}
                    iconProps={{
                      href: editIcon + "#globalG",
                      title: intl.formatMessage({
                        id: "appDomainCreation.formTitle.edit.button",
                      }),
                      strokeColor: "black",
                    }}
                    hoverColor="#ff3d00"
                    buttonSize={{height: 36, width: 36}}
                  />
                )}
              </span>
            </>
          )}
        </h1>
        <div className={"appDomainCreationContainer " + alternative}>
          {alternative === "create" ? (
            <p className="appDomainCreationExplanation">
              <FormattedMessage
                id="appDomainCreation.explanation"
                values={{
                  a: (chunks) => (
                    <a href={wikiUrl} target="_blank" rel="noreferrer">
                      {chunks}
                    </a>
                  ),
                }}
              />
            </p>
          ) : (
            <div></div>
          )}
          <h2>{<FormattedMessage id="appDomainCreation.h2.general" />}</h2>
          <TextInput
            label={intl.formatMessage({
              id: "appDomainCreation.inputLabel.name",
            })}
            idBase={"appDomainCreationName"}
            value={name}
            setValue={(v) => {
              setName(v);
              setNameError(undefined);
            }}
            isRequired
            isDisabled={waitingForResponse}
            isError={nameError !== undefined}
            supportingText={
              nameError ? intl.formatMessage({id: nameError}) : ""
            }
            isReadOnly={
              alternative === "view" ||
              props.viewEditInfo?.initialAppDomain.unresolved
            }
            isEdited={initialAppDomain && initialAppDomain.name !== name}
          />
          <DropDown<string>
            label={intl.formatMessage({id: "appDomainCreation.inputLabel.app"})}
            placeholder={intl.formatMessage({
              id: "appDomainCreation.inputLabel.app",
            })}
            idBase={"appDomainCreationApp"}
            options={appOptions ? appOptions : []}
            selectedOption={app}
            setSelectedOption={(o) => {
              setApp(o);
              setAppOptionsError(undefined);
            }}
            isRequired
            isDisabled={appOptions === undefined || waitingForResponse}
            isError={appOptionsError !== undefined}
            supportingText={
              appOptionsError ? intl.formatMessage({id: appOptionsError}) : ""
            }
            isReadOnly={
              alternative !== "create" ||
              props.viewEditInfo?.initialAppDomain.unresolved
            }
          />
          <TextArea
            label={intl.formatMessage({
              id: "appDomainCreation.inputLabel.description",
            })}
            idBase={"appDomainCreationDescription"}
            value={description}
            setValue={(v) => {
              if (v.trim().length > 0) setDescriptionError(false);
              setDescription(v);
            }}
            maxLength={850}
            rows={5}
            isError={descriptionError}
            onBlur={() => setDescriptionError(description.trim().length === 0)}
            supportingText={
              alternative !== "view"
                ? intl.formatMessage(
                    {
                      id: descriptionError
                        ? "reusables.defaultInputBehavior.error.required"
                        : "appDomainCreation.inputLabel.description.remaining",
                    },
                    {charCount: 850 - description.length},
                  )
                : ""
            }
            isDisabled={waitingForResponse}
            isReadOnly={
              alternative === "view" ||
              props.viewEditInfo?.initialAppDomain.unresolved
            }
            isEdited={
              initialAppDomain && initialAppDomain.description !== description
            }
            isRequired
          />
          {alternative !== "view" && (
            <div className="appDomainEditingButtonsContainer">
              {alternative === "create" ? (
                <>
                  <Button
                    label={intl.formatMessage({
                      id: "appDomainCreation.button.cancel",
                    })}
                    idBase={"appDomainCreationCancelCreation"}
                    onClick={() => {
                      if (emptyInputs) {
                        if (props.onCancel) props.onCancel();
                      } else
                        modalContext.showModal({
                          idBase: "appDomainCreationCancel",
                          headline: intl.formatMessage({
                            id: "appDomainCreation.modal.cancel.headline",
                          }),
                          body: intl.formatMessage({
                            id: "appDomainCreation.modal.cancel.text",
                          }),
                          primaryButton: {
                            name: intl.formatMessage({
                              id: "appDomainCreation.modal.cancel.cancel",
                            }),
                            onClick: () => {
                              if (props.onCancel) props.onCancel();
                            },
                          },
                          secondaryButton: {
                            name: intl.formatMessage({
                              id: "appDomainCreation.modal.cancel.back",
                            }),
                            onClick: () => undefined,
                          },
                        });
                    }}
                    isDisabled={waitingForResponse}
                    configuration={"tertiary"}
                  />
                  <Button
                    label={intl.formatMessage({
                      id: "appDomainCreation.button.save",
                    })}
                    idBase={"appDomainCreationSave"}
                    isDisabled={
                      waitingForResponse ||
                      !validInputs ||
                      !app ||
                      appOptionsError !== undefined ||
                      nameError !== undefined
                    }
                    isWide
                    onClick={onCreate}
                    configuration={"primary"}
                    isSubmit
                  />
                </>
              ) : (
                alternative === "edit" && (
                  <>
                    <Button
                      label={intl.formatMessage({
                        id: "appDomainCreation.button.cancel",
                      })}
                      idBase={"appDomainCreationCancelChanges"}
                      onClick={() => {
                        if (unmodifiedInputs) {
                          setInitialStates();
                          setAlternative("view");
                        } else
                          modalContext.showModal({
                            idBase: "appDomainCreationCancel",
                            headline: intl.formatMessage({
                              id: "appDomainCreation.modal.cancel.headline",
                            }),
                            body: intl.formatMessage({
                              id: "appDomainCreation.modal.cancel.text",
                            }),
                            primaryButton: {
                              name: intl.formatMessage({
                                id: "appDomainCreation.modal.cancel.cancel",
                              }),
                              onClick: () => {
                                setInitialStates();
                                setAlternative("view");
                              },
                            },
                            secondaryButton: {
                              name: intl.formatMessage({
                                id: "appDomainCreation.modal.cancel.back",
                              }),
                              onClick: () => undefined,
                            },
                          });
                      }}
                      isDisabled={
                        waitingForResponse ||
                        props.viewEditInfo?.initialAppDomain.unresolved
                      }
                      configuration={"tertiary"}
                    />
                    <Button
                      label={intl.formatMessage({
                        id: "appDomainCreation.button.save",
                      })}
                      idBase={"appDomainCreationSaveChanges"}
                      onClick={onUpdate}
                      isDisabled={
                        waitingForResponse ||
                        unmodifiedInputs ||
                        !validInputs ||
                        props.viewEditInfo?.initialAppDomain.unresolved
                      }
                      configuration={"primary"}
                      isSubmit
                    />
                  </>
                )
              )}
            </div>
          )}
        </div>
        {alternative !== "create" && (
          <div className="appDomainStreamsContainer">
            <h2>
              {
                <button
                  id="appDomainCreationStreamsCreate"
                  className="appDomainCreationStreamsCreate"
                  aria-label={intl.formatMessage({
                    id: "appDomainCreation.h2.streams.new",
                  })}
                  onClick={() => openStreamCreation()}
                >
                  <span>
                    <FormattedMessage id="appDomainCreation.h2.streams" />
                  </span>
                  <Icon
                    href={plusIcon + "#globalG"}
                    title={""}
                    iconSize={{width: 36, height: 36}}
                  />
                </button>
              }
            </h2>
            <div className="appDomainStreams">
              {appDomainStreams.length > 0 ? (
                appDomainStreams.map((stream, index) => (
                  <DataStreamChip
                    key={stream.id}
                    stream={stream}
                    index={index}
                    openStreamCreation={openStreamCreation}
                    deleteStream={deleteStream}
                    isAdmin={isAdmin}
                  />
                ))
              ) : (
                <p>
                  {
                    <FormattedMessage
                      id={
                        "appDomainCreation." +
                        (props.viewEditInfo?.initialAppDomain.unresolved
                          ? "loading"
                          : "noStreams")
                      }
                    />
                  }
                </p>
              )}
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

const DataStreamChip = (props: {
  stream: DataStreamWithWaiting;
  index: number;
  openStreamCreation: (stream: DataStream) => void;
  deleteStream: (stream: DataStream, index: number) => void;
  isAdmin: boolean;
}) => {
  const intl = useIntl();
  const editButton = (
    <IconButton
      idBase={"appDomainCreationEditStream" + props.index}
      onClick={() => props.openStreamCreation(props.stream)}
      iconProps={{
        href: editIcon + "#globalG",
        strokeColor: "white",
        title: intl.formatMessage({
          id: "appDomainCreation.editStream",
        }),
      }}
      buttonSize={{width: 48, height: 24}}
      disabled={props.stream.waitingForDeletion}
    />
  );
  const deleteButton = (
    <IconButton
      idBase={"appDomainCreationDeleteStream" + props.index}
      iconProps={{
        href: deleteIcon + "#globalG",
        strokeColor: "white",
        title: intl.formatMessage({
          id: "appDomainCreation.deleteStream",
        }),
      }}
      buttonSize={{width: 48, height: 24}}
      disabled={props.stream.waitingForDeletion}
      onClick={() => props.deleteStream(props.stream, props.index)}
    />
  );
  return (
    <div className={"appDomainStream" + (props.index === 0 ? " first" : "")}>
      {props.isAdmin && editButton}
      <AuxiliaryToolTip
        toolTip={props.stream.name}
        idBase={"appDomainCreationStreamName" + props.stream.id}
        tooltipPosition="top"
        overflowActivated
      >
        <div>
          <a
            id={`dataStreamChipLink${props.stream.name}${props.index}`}
            href={props.stream.url}
            target="_blank"
            rel="noreferrer"
          >
            {props.stream.name}
          </a>
        </div>
      </AuxiliaryToolTip>
      {props.isAdmin ? deleteButton : editButton}
    </div>
  );
};

export {AppDomainCreation};
